import { generateRoundRobin } from './roundRobinGenerator';
import { buildBracket, ordinal } from './bracketUtils';
import { suggestGroupCount, assignGroups } from './groupingUtils';
import { getGroupLabel } from './groupLabels';
import { newId } from './idGen';

export { suggestGroupCount };

/**
 * Generates group-stage pool play (real participants, real round robin) plus
 * a knockout bracket seeded from PLACEHOLDER qualifiers ("Group A · 1st place")
 * since actual standings don't exist until pool play is played out — this is
 * the standard "bracket shell" representation used before groups conclude.
 */
export const generateGroupKnockout = (participants, options = {}) => {
  const n = participants.length;
  if (n < 4) {
    throw new Error('Group + Knockout needs at least 4 participants');
  }

  const groupCount = Math.max(2, Math.min(options.groupCount || suggestGroupCount(n), Math.floor(n / 2)));
  const advancePerGroup = Math.max(1, options.advancePerGroup || 2);

  // An explicit, possibly hand-edited assignment from the group assignment
  // editor takes priority over the automatic serpentine split.
  const groupedParticipants = options.groupAssignments || assignGroups(participants, groupCount);

  const groupStage = groupedParticipants.map((groupParticipants, idx) => {
    const label = getGroupLabel('group_knockout', idx, options.groupNames);
    // `groupLabel` is tagged on every match HERE, not just in
    // buildExecutionOrder's merged copy below — the Groups tab
    // (GroupStandingsPanel) renders THIS `rounds` array directly, one
    // MatchListView per group, and the Filters panel's Group picker (see
    // scheduleRows.js's `rowMatchesFilters`) checks `row.groupLabel`
    // regardless of which tab/list it's looking at. Leaving it unset here
    // meant every row in a group's own list had `groupLabel: undefined`, so
    // selecting that very group in the Filters panel hid every match in it.
    const rounds = generateRoundRobin(groupParticipants).map((round) => ({
      ...round,
      matches: round.matches.map((m) => ({ ...m, groupLabel: label })),
    }));
    return {
      label,
      participants: groupParticipants,
      rounds,
      totalMatches: rounds.reduce((sum, r) => sum + r.matches.length, 0),
    };
  });

  const qualifiers = [];
  for (let rank = 1; rank <= advancePerGroup; rank++) {
    groupStage.forEach((group) => {
      qualifiers.push({
        id: `${group.label}-${rank}`,
        name: `${group.label} · ${ordinal(rank)} place`,
        isPlaceholder: true,
      });
    });
  }

  const knockout = buildBracket(qualifiers, 'knockout');

  return {
    formatId: 'group_knockout',
    participantsCount: n,
    groupCount,
    advancePerGroup,
    groupStage,
    knockout: knockout.rounds,
    knockoutBracketSize: knockout.bracketSize,
    executionOrder: buildExecutionOrder(groupStage, knockout.rounds),
  };
};

/**
 * Every match, across every group, merged into true execution order: all
 * groups play their Round 1 simultaneously, then all groups play Round 2,
 * etc. (not "finish Group A entirely, then start Group B") — then the
 * knockout stage follows once pool play concludes. Each group-stage match
 * keeps its own per-group number (e.g. Group A #1 / Group B #1 both existing
 * side by side is correct — they're simultaneous, not the same match) and is
 * tagged with `groupLabel` so the merged list can still show which group it
 * belongs to.
 */
const buildExecutionOrder = (groupStage, knockoutRounds) => {
  const maxGroupRounds = Math.max(...groupStage.map((g) => g.rounds.length));

  const groupRounds = [];
  for (let r = 0; r < maxGroupRounds; r++) {
    const matches = groupStage.flatMap((g) => {
      const round = g.rounds[r];
      if (!round) return [];
      return round.matches.map((m) => ({ ...m, groupLabel: g.label }));
    });
    groupRounds.push({ id: newId(), round: r + 1, label: `Group Stage — Round ${r + 1}`, matches });
  }

  const knockoutSection = knockoutRounds.map((r) => ({
    id: newId(),
    round: maxGroupRounds + r.round,
    label: `Knockout — ${r.label}`,
    matches: r.matches,
  }));

  return [...groupRounds, ...knockoutSection];
};
